import { Controller } from 'stimulus';

const DROPDOWN_GAP = 4;

export default class extends Controller {
  static targets = [
    'validator',
    'chips',
    'trigger',
    'dropdown',
    'search',
    'option',
    'checkbox',
    'empty',
    'template',
  ];

  #resizeObserver = null;
  #clippers = [];

  disconnect() {
    this.close();
  }

  toggle() {
    if (this.element.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open(event) {
    if (event?.target.closest('.remove')) {
      return;
    }

    if (this.element.classList.contains('is-open')) {
      this.searchTarget.focus();
      return;
    }

    this.element.classList.add('is-open');
    this.triggerTarget.setAttribute('aria-expanded', 'true');
    this.#clippers = this.#clippingAncestors();
    this.#reveal();
    this.dropdownTarget.showPopover();
    this.#anchor();
    window.addEventListener('resize', this.#reveal);
    window.addEventListener('scroll', this.#follow, true);
    this.#resizeObserver = new ResizeObserver(this.#reveal);
    this.#resizeObserver.observe(this.#control);
    this.searchTarget.focus();
  }

  close() {
    const hadFocus = this.dropdownTarget.contains(document.activeElement);
    this.element.classList.remove('is-open', 'is-flipped');
    this.triggerTarget.setAttribute('aria-expanded', 'false');
    window.removeEventListener('resize', this.#reveal);
    window.removeEventListener('scroll', this.#follow, true);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#clippers = [];

    if (this.dropdownTarget.matches(':popover-open')) {
      this.dropdownTarget.hidePopover();
    }

    if (this.searchTarget.value !== '') {
      this.searchTarget.value = '';
      this.searchTarget.dispatchEvent(new Event('input', { bubbles: true }));
    }

    if (hadFocus) {
      this.triggerTarget.focus({ preventScroll: true });
    }
  }

  dismiss(event) {
    if (!this.element.classList.contains('is-open')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  focusTrigger() {
    this.triggerTarget.focus();
  }

  keepFocus(event) {
    if (!event.target.closest('input, button')) {
      event.preventDefault();
    }
  }

  closeOnOutsideClick({ target }) {
    if (target.isConnected && !this.element.contains(target)) {
      this.close();
    }
  }

  closeOnFocusOut({ relatedTarget }) {
    if (relatedTarget && !this.element.contains(relatedTarget)) {
      this.close();
    }
  }

  closeOnRestore(event) {
    if (event.persisted) {
      this.close();
    }
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase();
    let matches = 0;

    for (const option of this.optionTargets) {
      const match = query === '' || option.dataset.label.toLowerCase().includes(query);
      option.hidden = !match;
      matches += match ? 1 : 0;
    }

    this.emptyTarget.hidden = matches > 0;
  }

  navigate(event) {
    if (event.isComposing || event.keyCode === 229) {
      return;
    }

    const checkboxes = this.checkboxTargets.filter((checkbox) => checkbox.offsetParent !== null);
    const index = checkboxes.indexOf(document.activeElement);

    if (event.key === 'ArrowDown' && checkboxes.length > 0) {
      event.preventDefault();
      checkboxes[Math.min(index + 1, checkboxes.length - 1)].focus();
    } else if (event.key === 'ArrowUp' && index >= 0) {
      event.preventDefault();
      (checkboxes[index - 1] ?? this.searchTarget).focus();
    } else if (event.key === 'Enter' && event.target === this.searchTarget) {
      event.preventDefault();
      checkboxes[0]?.click();
    }
  }

  sync() {
    const checked = this.checkboxTargets.filter((checkbox) => checkbox.checked);

    this.chipsTarget.replaceChildren(...checked.map((checkbox) => this.#chip(checkbox)));
    this.element.classList.toggle('is-empty', checked.length === 0);

    if (this.hasValidatorTarget) {
      this.validatorTarget.value = checked.length > 0 ? String(checked.length) : '';
      this.validatorTarget.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.dispatch('change', { detail: { values: checked.map((checkbox) => checkbox.value) } });
  }

  remove({ target, params }) {
    const checkbox = this.checkboxTargets.find(
      (candidate) => candidate.value === String(params.value)
    );
    const index = Array.from(this.chipsTarget.children).indexOf(target.closest('.chip'));

    if (!checkbox) {
      return;
    }

    checkbox.checked = false;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    (this.chipsTarget.children[index]?.querySelector('.remove') ?? this.triggerTarget).focus();
  }

  #chip(checkbox) {
    const option = checkbox.closest('.option');
    const chip = this.templateTarget.content.firstElementChild.cloneNode(true);
    const label = option.dataset.label;

    chip.dataset.value = checkbox.value;
    chip.querySelector('.text').textContent = label;
    chip.querySelector('.remove').setAttribute('aria-label', `Remove ${label}`);
    chip.querySelector('.remove').setAttribute('data-ui--tag-select-value-param', checkbox.value);

    return chip;
  }

  get #control() {
    return this.element.querySelector('.control');
  }

  #reveal = () => {
    this.#control.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    this.#anchor();
  };

  #follow = () => {
    if (this.#controlHidden()) {
      this.close();
    } else {
      this.#anchor();
    }
  };

  #anchor() {
    const { top, left, bottom, width } = this.#control.getBoundingClientRect();
    const { flipped, maxHeight } = this.#place(top, bottom);

    this.element.classList.toggle('is-flipped', flipped);
    this.dropdownTarget.style.setProperty('--tag-select-top', `${bottom}px`);
    this.dropdownTarget.style.setProperty('--tag-select-bottom', `${window.innerHeight - top}px`);
    this.dropdownTarget.style.setProperty('--tag-select-left', `${left}px`);
    this.dropdownTarget.style.setProperty('--tag-select-width', `${width}px`);

    if (maxHeight) {
      this.dropdownTarget.style.setProperty('--tag-select-max-height', `${maxHeight}px`);
    }
  }

  #place(top, bottom) {
    this.dropdownTarget.style.removeProperty('--tag-select-max-height');

    const needed = this.dropdownTarget.offsetHeight + DROPDOWN_GAP;
    const clip = this.#clipRect();
    const below = clip.bottom - bottom;
    const above = top - clip.top;
    const belowViewport = window.innerHeight - bottom;
    const aboveViewport = top;

    if (needed <= below) {
      return { flipped: false };
    }

    if (needed <= above) {
      return { flipped: true };
    }

    if (needed <= belowViewport) {
      return { flipped: false };
    }

    if (needed <= aboveViewport) {
      return { flipped: true };
    }

    const flipped = aboveViewport > belowViewport;

    return { flipped, maxHeight: (flipped ? aboveViewport : belowViewport) - DROPDOWN_GAP };
  }

  #controlHidden() {
    const { top, bottom } = this.#control.getBoundingClientRect();
    const clip = this.#clipRect();

    return bottom <= clip.top || top >= clip.bottom;
  }

  #clipRect() {
    let top = 0;
    let bottom = window.innerHeight;

    for (const clipper of this.#clippers) {
      const rect = clipper.getBoundingClientRect();
      top = Math.max(top, rect.top);
      bottom = Math.min(bottom, rect.bottom);
    }

    return { top, bottom };
  }

  #clippingAncestors() {
    const clippers = [];

    for (let node = this.#control.parentElement; node; node = node.parentElement) {
      if (getComputedStyle(node).overflowY !== 'visible') {
        clippers.push(node);
      }

      if (node.matches(':modal')) {
        break;
      }
    }

    return clippers;
  }
}
